import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import DesignModule from "./DesignModule";
import { api } from "./api";

const Drawings3D = ({ project }) => {
  const navigate = useNavigate();

  const projectId = project?._id || project?.id;

  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const projectName =
    project?.name ||
    project?.projectName ||
    project?.leadName ||
    "Project Management Dashboard";

  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem("user");
      const parsedUser = userData ? JSON.parse(userData) : null;

      return {
        userId: parsedUser?._id || parsedUser?.id || null,
        userName:
          parsedUser?.name ||
          parsedUser?.userName ||
          localStorage.getItem("userName") ||
          localStorage.getItem("name") ||
          "Unknown User",
      };
    } catch (error) {
      return {
        userId: null,
        userName:
          localStorage.getItem("userName") ||
          localStorage.getItem("name") ||
          "Unknown User",
      };
    }
  };

  const loadComments = async () => {
    if (!projectId) return;

    try {
      setLoadingComments(true);
      const res = await api.get(`/drawing-design/${projectId}/comments`);
      setComments(res.data || []);
    } catch (error) {
      console.error("Load comments error:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [projectId]);

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    if (!projectId) {
      alert("Project ID missing");
      return;
    }

    try {
      setSavingComment(true);

      const currentUser = getCurrentUser();

      const res = await api.post(`/drawing-design/${projectId}/comments`, {
        comment: comment.trim(),
        userId: currentUser.userId,
        userName: currentUser.userName,
      });

      setComments((prev) => [res.data, ...prev]);
      setComment("");
    } catch (error) {
      console.error("Comment save error:", error);
      alert("Failed to save comment");
    } finally {
      setSavingComment(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <button onClick={() => navigate(-1)} style={styles.backBtn}>
            ← Back
          </button>

          <div style={{ textAlign: "center" }}>
            <h1 style={styles.heading}>Drawings & 3D</h1>
            <p style={styles.subHeading}>Design Management Dashboard</p>
          </div>

          <div style={{ width: "95px" }} />
        </div>
      </div>

      {/* Project Card */}
      <div style={styles.container}>
        <div style={styles.projectCard}>
          <div>
            <p style={styles.label}>Current Project</p>
            <h2 style={styles.projectName}>{projectName}</h2>
          </div>

          <div style={styles.badge}>DESIGN MODULE</div>
        </div>
      </div>

      {/* Design Module */}
      <div style={styles.container}>
        <div style={styles.moduleCard}>
          <div style={styles.moduleHeader}></div>

          <div style={{ padding: "24px" }}>
            <DesignModule project={project} />
          </div>
        </div>
      </div>

      {/* Comment Section */}
      <div style={styles.container}>
        <div style={styles.commentCard}>
          <div style={styles.commentHeader}>
            <h3 style={styles.commentTitle}>Comments</h3>
            <span style={styles.commentCount}>{comments.length}</span>
          </div>

          <div style={styles.inputBox}>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your comment here..."
              style={styles.textarea}
            />

            <button
              onClick={handleAddComment}
              disabled={savingComment}
              style={{
                ...styles.commentBtn,
                opacity: savingComment ? 0.6 : 1,
                cursor: savingComment ? "not-allowed" : "pointer",
              }}
            >
              {savingComment ? "Saving..." : "Add Comment"}
            </button>
          </div>

          <div style={styles.commentList}>
            {loadingComments ? (
              <div style={styles.emptyComment}>Loading comments...</div>
            ) : comments.length === 0 ? (
              <div style={styles.emptyComment}>
                No comments yet. Add first comment.
              </div>
            ) : (
              comments.map((item) => (
                <div key={item._id || item.id} style={styles.commentItem}>
                  <div style={styles.avatar}>
                    {item.userName?.charAt(0)?.toUpperCase() || "U"}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={styles.commentTop}>
                      <strong>{item.userName || "Unknown User"}</strong>
                      <span>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : "-"}
                      </span>
                    </div>

                    <p style={styles.commentText}>
                      {item.comment || item.text}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
  },

  header: {
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  },

  headerInner: {
    maxWidth: "1500px",
    margin: "0 auto",
    padding: "18px 24px",
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: "18px",
  },

  backBtn: {
    padding: "11px 18px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "700",
    color: "#111827",
  },

  heading: {
    margin: 0,
    fontSize: "34px",
    fontWeight: "900",
    color: "#0f172a",
  },

  subHeading: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "15px",
    fontWeight: "600",
  },

  container: {
    maxWidth: "1500px",
    margin: "24px auto 0",
    padding: "0 24px",
  },

  projectCard: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "22px 26px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "18px",
  },

  label: {
    margin: "0 0 6px",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "800",
    textTransform: "uppercase",
  },

  projectName: {
    margin: 0,
    color: "#111827",
    fontSize: "26px",
    fontWeight: "900",
  },

  badge: {
    padding: "10px 18px",
    borderRadius: "999px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "14px",
    fontWeight: "800",
    border: "1px solid #86efac",
  },

  moduleCard: {
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
    overflow: "hidden",
    minHeight: "600px",
  },

  moduleHeader: {
    padding: "18px 24px",
    borderBottom: "1px solid #e5e7eb",
    background: "linear-gradient(90deg, #eff6ff, #f8fafc)",
  },

  moduleTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "900",
    color: "#1e293b",
  },

  moduleSubTitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "14px",
    fontWeight: "600",
  },

  commentCard: {
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
    padding: "24px",
    marginBottom: "34px",
  },

  commentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "18px",
  },

  commentTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "900",
    color: "#1e293b",
  },

  commentCount: {
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: "800",
  },

  inputBox: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "22px",
  },

  textarea: {
    flex: 1,
    minHeight: "90px",
    resize: "vertical",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    padding: "14px",
    fontSize: "15px",
    outline: "none",
  },

  commentBtn: {
    padding: "13px 20px",
    borderRadius: "12px",
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
  },

  commentList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  emptyComment: {
    padding: "20px",
    borderRadius: "14px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: "600",
    textAlign: "center",
  },

  commentItem: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
  },

  avatar: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    background: "#2563eb",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "900",
    flexShrink: 0,
  },

  commentTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#334155",
    fontSize: "14px",
    flexWrap: "wrap",
  },

  commentText: {
    margin: "8px 0 0",
    color: "#0f172a",
    fontSize: "15px",
    lineHeight: "1.5",
  },
};

export default Drawings3D;
